import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Calendar,
  Mic,
  ChevronLeft,
  Clock,
  Plus,
  Upload,
  Trash2,
  Edit2,
  MapPin,
  AlertCircle,
  CheckCircle2,
  Loader2,
  GraduationCap,
  Target,
  TrendingUp
} from 'lucide-react';
import { formatDistanceToNow, format, isPast, differenceInDays } from 'date-fns';
import { LectureAiChip, SurfaceHero } from '../components/design-kit';

// Countdown badge component
const CountdownBadge = ({ examDate }) => {
  const now = new Date();
  const date = new Date(examDate);
  const days = differenceInDays(date, now);
  const past = isPast(date);
  
  if (past) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
        Completed
      </span>
    );
  }
  
  if (days === 0) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500 text-white animate-pulse">
        Today!
      </span>
    );
  }
  
  if (days === 1) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/90 text-white">
        Tomorrow
      </span>
    );
  }
  
  if (days <= 3) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-500 text-white">
        {days} days
      </span>
    );
  }
  
  if (days <= 7) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500 text-white">
        {days} days
      </span>
    );
  }
  
  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
      {days} days
    </span>
  );
};

// Exam card component
const ExamCard = ({ exam, onEdit, onDelete, onAddScore, onReview }) => {
  const examDate = new Date(exam.exam_date);
  const past = isPast(examDate);
  
  return (
    <Card className={`border-border/50 shadow-card ${past ? 'opacity-80' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {exam.subject_color && (
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: exam.subject_color }}
                />
              )}
              <h3 className="font-medium text-foreground truncate">{exam.title}</h3>
            </div>
            
            {exam.subject_name && (
              <p className="text-sm text-muted-foreground mb-2">{exam.subject_name}</p>
            )}
            
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {format(examDate, 'MMM d, yyyy')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {format(examDate, 'h:mm a')}
              </span>
              {exam.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {exam.location}
                </span>
              )}
            </div>
            
            {/* Score display for past exams */}
            {past && exam.has_score && (
              <div className="mt-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium">
                  Score: {exam.score}/{exam.max_score} ({Math.round(exam.score / exam.max_score * 100)}%)
                </span>
              </div>
            )}
            
            {past && !exam.has_score && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => onAddScore(exam)}
              >
                <Target className="w-3.5 h-3.5 mr-1" />
                Add Score
              </Button>
            )}

            {!past && exam.voice_review_href && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => onReview(exam)}
              >
                <Mic className="w-3.5 h-3.5 mr-1" />
                Voice Review
              </Button>
            )}
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <CountdownBadge examDate={exam.exam_date} />
            
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(exam)}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onDelete(exam)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ExamSchedulePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') === 'past' ? 'past' : 'upcoming';
  
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [studyDashboard, setStudyDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(requestedTab);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    exam_date: '',
    exam_time: '',
    subject_id: 'none',
    weight: 1.0,
    location: '',
    duration_minutes: '',
    notes: ''
  });
  const [scoreData, setScoreData] = useState({
    score: '',
    max_score: '100',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadData();
    
    // Check if we should open score dialog from URL
    const scoreExamId = searchParams.get('score');
    if (scoreExamId) {
      // Will open score dialog once exams are loaded
    }
  }, []);

  useEffect(() => {
    if (requestedTab !== activeTab) {
      setActiveTab(requestedTab);
    }
  }, [requestedTab, activeTab]);

  // Handle score param from URL
  useEffect(() => {
    const scoreExamId = searchParams.get('score');
    if (scoreExamId && exams.length > 0) {
      const exam = exams.find(e => e.id === scoreExamId);
      if (exam && !exam.has_score) {
        handleAddScore(exam);
        // Clear the param
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('score');
        setSearchParams(nextParams);
      }
    }
  }, [exams, searchParams]);

  const loadData = async () => {
    try {
      const [examsData, subjectsData] = await Promise.all([
        api.getExams(),
        api.getSubjects()
      ]);
      setExams(examsData);
      setSubjects(subjectsData);
      setStudyDashboard(await api.getStudyDashboard().catch(() => null));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrEdit = async () => {
    if (!formData.title || !formData.exam_date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Combine date and time
      let examDateTime = formData.exam_date;
      if (formData.exam_time) {
        examDateTime = `${formData.exam_date}T${formData.exam_time}:00`;
      } else {
        examDateTime = `${formData.exam_date}T09:00:00`;
      }
      
      const data = {
        title: formData.title,
        exam_date: new Date(examDateTime).toISOString(),
        subject_id: formData.subject_id === 'none' ? null : formData.subject_id,
        weight: parseFloat(formData.weight) || 1.0,
        location: formData.location || null,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        notes: formData.notes || null
      };
      
      if (selectedExam) {
        await api.updateExam(selectedExam.id, data);
        toast.success('Exam updated');
      } else {
        await api.createExam(data);
        toast.success('Exam created');
      }
      
      setShowCreateDialog(false);
      setSelectedExam(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExam) return;
    
    try {
      await api.deleteExam(selectedExam.id);
      toast.success('Exam deleted');
      setShowDeleteDialog(false);
      setSelectedExam(null);
      loadData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSubmitScore = async () => {
    if (!selectedExam || !scoreData.score) {
      toast.error('Please enter a score');
      return;
    }
    
    setSaving(true);
    try {
      await api.submitExamScore(
        selectedExam.id,
        parseFloat(scoreData.score),
        parseFloat(scoreData.max_score) || 100,
        scoreData.notes || null
      );
      toast.success('Score recorded');
      setShowScoreDialog(false);
      setSelectedExam(null);
      setScoreData({ score: '', max_score: '100', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImportICS = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const result = await api.importICS(file);
      toast.success(`Imported ${result.imported} exams`);
      if (result.skipped > 0) {
        toast.info(`${result.skipped} events skipped (duplicates or no date)`);
      }
      loadData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setImporting(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleEdit = (exam) => {
    const examDate = new Date(exam.exam_date);
    setSelectedExam(exam);
    setFormData({
      title: exam.title,
      exam_date: format(examDate, 'yyyy-MM-dd'),
      exam_time: format(examDate, 'HH:mm'),
      subject_id: exam.subject_id || 'none',
      weight: exam.weight || 1.0,
      location: exam.location || '',
      duration_minutes: exam.duration_minutes?.toString() || '',
      notes: exam.notes || ''
    });
    setShowCreateDialog(true);
  };

  const handleAddScore = (exam) => {
    setSelectedExam(exam);
    setScoreData({
      score: exam.score?.toString() || '',
      max_score: exam.max_score?.toString() || '100',
      notes: ''
    });
    setShowScoreDialog(true);
  };

  const handleDeleteClick = (exam) => {
    setSelectedExam(exam);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      exam_date: '',
      exam_time: '',
      subject_id: 'none',
      weight: 1.0,
      location: '',
      duration_minutes: '',
      notes: ''
    });
  };

  // Filter exams
  const upcomingExams = exams.filter(e => !isPast(new Date(e.exam_date)));
  const pastExams = exams.filter(e => isPast(new Date(e.exam_date)));
  const examsNeedingScores = pastExams.filter(e => !e.has_score);
  const nextExam = studyDashboard?.upcoming_exams?.[0] || upcomingExams[0] || null;
  const recommendedSubjectId = studyDashboard?.recommendation?.subject_id || nextExam?.subject_id || null;

  const handleStartVoiceReview = (exam) => {
    if (exam?.voice_review_href) {
      navigate(exam.voice_review_href);
      return;
    }
    if (exam?.subject_id) {
      navigate(`/qna-review?subject=${exam.subject_id}&mode=voice`);
      return;
    }
    navigate('/qna-review?mode=voice');
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'past') {
      nextParams.set('tab', 'past');
    } else {
      nextParams.delete('tab');
    }
    setSearchParams(nextParams);
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-auto min-h-16 max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground">Exam Schedule</span>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <input
              type="file"
              accept=".ics,.ical"
              onChange={handleImportICS}
              className="hidden"
              id="ics-upload"
              disabled={importing}
            />
            <label htmlFor="ics-upload">
              <Button variant="outline" size="sm" className="rounded-xl" asChild disabled={importing}>
                <span>
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Import ICS
                </span>
              </Button>
            </label>
            <Button 
              size="sm" 
              className="rounded-xl"
              onClick={() => {
                setSelectedExam(null);
                resetForm();
                setShowCreateDialog(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Exam
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <SurfaceHero
          eyebrow="Exam Planning"
          title="Exam Schedule"
          detail="Track deadlines, record outcomes, and jump straight into the right study mode for the next exam that matters."
          className="mb-8"
          aside={
            recommendedSubjectId ? (
              <Button
                className="rounded-xl"
                onClick={() => navigate(`/qna-review?subject=${recommendedSubjectId}&mode=voice`)}
              >
                <Mic className="w-4 h-4 mr-2" />
                Start Voice Review
              </Button>
            ) : null
          }
        />

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <Card className="border-border/60 bg-card/80 shadow-card">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Next Exam</p>
              {nextExam ? (
                <>
                  <p className="mt-3 font-serif text-xl text-foreground">{nextExam.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {nextExam.days_until === 0 ? 'Today' : `${nextExam.days_until} day${nextExam.days_until === 1 ? '' : 's'} away`}
                  </p>
                </>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">No upcoming exams scheduled.</p>
              )}
            </CardContent>
          </Card>
          <Card className="border-border/60 bg-card/80 shadow-card">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Scores Missing</p>
              <p className="mt-3 font-serif text-xl text-foreground">{examsNeedingScores.length}</p>
              <p className="mt-1 text-sm text-muted-foreground">Past exams that still need a recorded result.</p>
            </CardContent>
          </Card>
          <Card className="border-border/60 bg-card/80 shadow-card">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Recommended Focus</p>
              {studyDashboard?.recommendation ? (
                <>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <LectureAiChip
                      label={studyDashboard.recommendation.subject_name}
                      tone="subject"
                      active
                    />
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{studyDashboard.recommendation.recommendation}</p>
                </>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">Add exams to unlock tailored scheduling guidance.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Exams needing scores alert */}
        {examsNeedingScores.length > 0 && (
          <Card className="mb-6 border-amber-500/50 bg-amber-500/5">
            <CardContent className="flex flex-col items-start gap-3 py-4 sm:flex-row sm:items-center sm:gap-4">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-foreground">
                  {examsNeedingScores.length} exam{examsNeedingScores.length !== 1 ? 's' : ''} need scores
                </p>
                <p className="text-sm text-muted-foreground">
                  Recording your scores helps optimize your study recommendations
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleTabChange('past')}
              >
                View
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6">
            <TabsTrigger value="upcoming" className="gap-2">
              <Calendar className="w-4 h-4" />
              Upcoming ({upcomingExams.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Past ({pastExams.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : upcomingExams.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-foreground mb-2">No upcoming exams</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Add your exam schedule to get study recommendations
                  </p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Exam
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {upcomingExams.map(exam => (
                  <ExamCard
                    key={exam.id}
                    exam={exam}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onAddScore={handleAddScore}
                    onReview={handleStartVoiceReview}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-24 w-full" />
                ))}
              </div>
            ) : pastExams.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <TrendingUp className="w-12 h-12 text-muted-foreground/50 mb-4" />
                  <h3 className="font-medium text-foreground mb-2">No past exams</h3>
                  <p className="text-muted-foreground text-sm">
                    Completed exams will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pastExams.map(exam => (
                  <ExamCard
                    key={exam.id}
                    exam={exam}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onAddScore={handleAddScore}
                    onReview={handleStartVoiceReview}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedExam ? 'Edit Exam' : 'Add Exam'}</DialogTitle>
            <DialogDescription>
              {selectedExam ? 'Update exam details' : 'Add a new exam to your schedule'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., MATH 201 Midterm"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="exam_date">Date *</Label>
                <Input
                  id="exam_date"
                  type="date"
                  value={formData.exam_date}
                  onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exam_time">Time</Label>
                <Input
                  id="exam_time"
                  type="time"
                  value={formData.exam_time}
                  onChange={(e) => setFormData({ ...formData, exam_time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Select
                value={formData.subject_id}
                onValueChange={(value) => setFormData({ ...formData, subject_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No subject</SelectItem>
                  {subjects.map(subject => (
                    <SelectItem key={subject.id} value={subject.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: subject.color }}
                        />
                        {subject.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="weight">Weight (importance)</Label>
                <Select
                  value={formData.weight.toString()}
                  onValueChange={(value) => setFormData({ ...formData, weight: parseFloat(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.1">10% - Quiz</SelectItem>
                    <SelectItem value="0.2">20% - Minor</SelectItem>
                    <SelectItem value="0.3">30% - Midterm</SelectItem>
                    <SelectItem value="0.5">50% - Major</SelectItem>
                    <SelectItem value="1.0">100% - Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  placeholder="e.g., 90"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="e.g., Room 101"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrEdit} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedExam ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Score Dialog */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Record Exam Score</DialogTitle>
            <DialogDescription>
              {selectedExam?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="score">Your Score *</Label>
                <Input
                  id="score"
                  type="number"
                  placeholder="e.g., 85"
                  value={scoreData.score}
                  onChange={(e) => setScoreData({ ...scoreData, score: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_score">Max Score</Label>
                <Input
                  id="max_score"
                  type="number"
                  placeholder="100"
                  value={scoreData.max_score}
                  onChange={(e) => setScoreData({ ...scoreData, max_score: e.target.value })}
                />
              </div>
            </div>
            
            {scoreData.score && scoreData.max_score && (
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="text-3xl font-bold text-primary">
                  {Math.round(parseFloat(scoreData.score) / parseFloat(scoreData.max_score) * 100)}%
                </div>
                <div className="text-sm text-muted-foreground">
                  {parseFloat(scoreData.score)} / {parseFloat(scoreData.max_score)}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="score_notes">Notes (optional)</Label>
              <Textarea
                id="score_notes"
                placeholder="How did it go? Areas to improve?"
                value={scoreData.notes}
                onChange={(e) => setScoreData({ ...scoreData, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScoreDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitScore} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Exam</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedExam?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
